import { useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Upload, 
  FileImage, 
  FileText,
  Loader2, 
  ArrowRight,
  ArrowUpDown,
  TrendingUp,
  TrendingDown,
  Ruler,
  Square,
  CircleDashed,
  TreePine,
  Fence,
  Info,
  CheckCircle,
  AlertTriangle,
  Calculator,
  Clock,
  DollarSign,
  Eye,
  EyeOff,
  Check,
  X as XIcon,
  Layers,
  Scale,
  MessageCircle,
  Send,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Nomenclature complète des éléments paysagers (Norme Canadienne du Paysage)
const elementLabels = {
  // Préparation
  earthwork: "Terrassement",
  drainage: "Drainage",
  // Maçonnerie
  border: "Bordures",
  paver: "Pavage / Dallage",
  wall: "Murets / Soutènements",
  terrace: "Terrasse",
  stairs: "Escaliers",
  // Clôtures
  fence: "Clôtures",
  gate: "Portails",
  // Végétation
  lawn: "Gazon / Pelouse",
  tree: "Arbres",
  shrub: "Arbustes",
  hedge: "Haies",
  perennial: "Vivaces / Massifs",
  bulb: "Bulbes",
  // Couverture
  mulch: "Paillage",
  geotextile: "Géotextile",
  // Eau
  irrigation: "Arrosage auto.",
  pond: "Bassin / Point d'eau",
  // Équipements
  furniture: "Mobilier",
  lighting: "Éclairage",
  // Structures
  building: "Bâtiments",
  pergola: "Pergola / Tonnelle"
};

const elementUnits = {
  // Préparation
  earthwork: "m³",
  drainage: "m",
  // Maçonnerie
  border: "m",
  paver: "m²",
  wall: "m",
  terrace: "m²",
  stairs: "p",
  // Clôtures
  fence: "m",
  gate: "p",
  // Végétation
  lawn: "m²",
  tree: "p",
  shrub: "p",
  hedge: "m",
  perennial: "m²",
  bulb: "p",
  // Couverture
  mulch: "m²",
  geotextile: "m²",
  // Eau
  irrigation: "m²",
  pond: "m²",
  // Équipements
  furniture: "p",
  lighting: "p",
  // Structures
  building: "m²",
  pergola: "m²"
};

const elementColors = {
  // Préparation
  earthwork: { bg: "bg-stone-100", text: "text-stone-700", hex: "#78716c" },
  drainage: { bg: "bg-cyan-100", text: "text-cyan-700", hex: "#06b6d4" },
  // Maçonnerie
  border: { bg: "bg-orange-100", text: "text-orange-700", hex: "#f97316" },
  paver: { bg: "bg-purple-100", text: "text-purple-700", hex: "#a855f7" },
  wall: { bg: "bg-gray-100", text: "text-gray-700", hex: "#6b7280" },
  terrace: { bg: "bg-amber-100", text: "text-amber-700", hex: "#f59e0b" },
  stairs: { bg: "bg-rose-100", text: "text-rose-700", hex: "#f43f5e" },
  // Clôtures
  fence: { bg: "bg-blue-100", text: "text-blue-700", hex: "#3b82f6" },
  gate: { bg: "bg-indigo-100", text: "text-indigo-700", hex: "#6366f1" },
  // Végétation
  lawn: { bg: "bg-green-100", text: "text-green-700", hex: "#22c55e" },
  tree: { bg: "bg-emerald-100", text: "text-emerald-700", hex: "#10b981" },
  shrub: { bg: "bg-lime-100", text: "text-lime-700", hex: "#84cc16" },
  hedge: { bg: "bg-teal-100", text: "text-teal-700", hex: "#14b8a6" },
  perennial: { bg: "bg-pink-100", text: "text-pink-700", hex: "#ec4899" },
  bulb: { bg: "bg-fuchsia-100", text: "text-fuchsia-700", hex: "#d946ef" },
  // Couverture
  mulch: { bg: "bg-yellow-100", text: "text-yellow-700", hex: "#eab308" },
  geotextile: { bg: "bg-neutral-100", text: "text-neutral-700", hex: "#737373" },
  // Eau
  irrigation: { bg: "bg-sky-100", text: "text-sky-700", hex: "#0ea5e9" },
  pond: { bg: "bg-blue-200", text: "text-blue-800", hex: "#1d4ed8" },
  // Équipements
  furniture: { bg: "bg-orange-200", text: "text-orange-800", hex: "#ea580c" },
  lighting: { bg: "bg-yellow-200", text: "text-yellow-800", hex: "#ca8a04" },
  // Structures
  building: { bg: "bg-slate-200", text: "text-slate-800", hex: "#475569" },
  pergola: { bg: "bg-amber-200", text: "text-amber-800", hex: "#d97706" }
};

// Composant de visualisation du plan avec overlay
const PlanVisualization = ({ preview, elements, showOverlay }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (!preview || !canvasRef.current || !showOverlay || !elements?.length) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const container = containerRef.current;
    
    const img = new Image();
    img.onload = () => {
      const containerWidth = container.clientWidth;
      const scale = containerWidth / img.width;
      canvas.width = containerWidth;
      canvas.height = img.height * scale;
      
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Dessiner les indicateurs
      elements.forEach((elem, idx) => {
        const color = elementColors[elem.type]?.hex || '#9ca3af';
        
        // Position sur le côté gauche
        const x = 25;
        const y = 35 + idx * 55;
        
        // Cercle numéroté
        ctx.beginPath();
        ctx.arc(x, y, 15, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText((idx + 1).toString(), x, y);
        
        // Ligne pointillée
        ctx.beginPath();
        ctx.moveTo(x + 15, y);
        ctx.lineTo(x + 60, y);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 4]);
        ctx.stroke();
        ctx.setLineDash([]);
        
        // Label
        ctx.fillStyle = '#1f2937';
        ctx.font = '11px sans-serif';
        ctx.textAlign = 'left';
        const label = `${elementLabels[elem.type] || elem.type}: ${elem.length_m || elem.area_m2}${elementUnits[elem.type] || ''}`;
        ctx.fillText(label, x + 65, y + 4);
      });
    };
    img.src = preview;
  }, [preview, elements, showOverlay]);
  
  return (
    <div ref={containerRef} className="relative w-full rounded-xl overflow-hidden border-2 border-gray-200">
      {showOverlay && elements?.length ? (
        <canvas ref={canvasRef} className="w-full" />
      ) : preview ? (
        <img src={preview} alt="Plan" className="w-full" />
      ) : null}
    </div>
  );
};

// Composant de comparaison de métrés
const MetricComparison = ({ elementType, planValue, requestValue, selectedValue, onValueSelect, onCustomValue }) => {
  const [showCustom, setShowCustom] = useState(false);
  const [customVal, setCustomVal] = useState('');
  
  const unit = elementUnits[elementType] || '';
  const colors = elementColors[elementType] || elementColors.border;
  
  const diff = requestValue > 0 ? ((planValue - requestValue) / requestValue * 100) : 0;
  const diffSeverity = Math.abs(diff) > 20 ? 'high' : Math.abs(diff) > 10 ? 'medium' : 'low';
  
  const handleCustomSubmit = () => {
    const val = parseFloat(customVal);
    if (!isNaN(val) && val >= 0) {
      onCustomValue(val);
      setShowCustom(false);
    }
  };
  
  return (
    <div className={`p-4 rounded-xl border-2 ${colors.bg} transition-all`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-800">{elementLabels[elementType]}</span>
          {requestValue > 0 && planValue > 0 && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              diffSeverity === 'high' ? 'bg-red-200 text-red-800' :
              diffSeverity === 'medium' ? 'bg-yellow-200 text-yellow-800' :
              'bg-green-200 text-green-800'
            }`}>
              {diff > 0 ? '+' : ''}{diff.toFixed(1)}%
            </span>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-2 mb-3">
        {/* Valeur de la demande */}
        <button
          onClick={() => onValueSelect('request', requestValue)}
          className={`p-3 rounded-lg border-2 transition-all ${
            selectedValue?.source === 'request' 
              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <p className="text-xs text-gray-500 mb-1">Demande</p>
          <p className="text-lg font-bold text-gray-800">{requestValue || '--'}</p>
          <p className="text-xs text-gray-500">{unit}</p>
        </button>
        
        {/* Valeur du plan (IA) */}
        <button
          onClick={() => onValueSelect('plan', planValue)}
          className={`p-3 rounded-lg border-2 transition-all ${
            selectedValue?.source === 'plan' 
              ? 'border-green-500 bg-green-50 ring-2 ring-green-200' 
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <p className="text-xs text-gray-500 mb-1">Plan (IA)</p>
          <p className="text-lg font-bold text-gray-800">{planValue || '--'}</p>
          <p className="text-xs text-gray-500">{unit}</p>
        </button>
        
        {/* Valeur personnalisée */}
        <button
          onClick={() => setShowCustom(!showCustom)}
          className={`p-3 rounded-lg border-2 transition-all ${
            selectedValue?.source === 'custom' 
              ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' 
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <p className="text-xs text-gray-500 mb-1">Personnalisé</p>
          <p className="text-lg font-bold text-gray-800">
            {selectedValue?.source === 'custom' ? selectedValue.value : '--'}
          </p>
          <p className="text-xs text-gray-500">{unit}</p>
        </button>
      </div>
      
      {/* Input personnalisé */}
      {showCustom && (
        <div className="flex items-center gap-2 mt-2 p-2 bg-white rounded-lg">
          <Input
            type="number"
            value={customVal}
            onChange={(e) => setCustomVal(e.target.value)}
            placeholder="Entrez une valeur"
            className="flex-1"
          />
          <span className="text-sm text-gray-500">{unit}</span>
          <Button size="sm" onClick={handleCustomSubmit}>
            <Check className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowCustom(false)}>
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      {/* Valeur sélectionnée */}
      {selectedValue && (
        <div className="mt-2 p-2 bg-white/70 rounded-lg flex items-center justify-between">
          <span className="text-sm text-gray-600">Valeur retenue:</span>
          <span className="font-bold text-gray-800">{selectedValue.value} {unit}</span>
        </div>
      )}
    </div>
  );
};

// Composant Chat de modification
const QuoteChat = ({ quoteData, onQuoteUpdate }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);
  
  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/quotes/chat`, {
        session_id: `quote-${Date.now()}`,
        message: userMessage,
        quote_data: quoteData
      }, { headers: getAuthHeaders() });
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        actions: response.data.actions_taken
      }]);
      
      if (response.data.updated_quote) {
        onQuoteUpdate(response.data.updated_quote);
      }
      
    } catch (error) {
      toast.error("Erreur lors de l'envoi du message");
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Désolé, une erreur s'est produite. Veuillez réessayer."
      }]);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageCircle className="h-5 w-5 text-[#1E8B5A]" />
          Modifier le devis
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64 overflow-y-auto mb-3 space-y-2 p-2 bg-gray-50 rounded-lg">
          {messages.length === 0 && (
            <div className="text-center text-gray-500 text-sm py-8">
              <p>Exemples de commandes:</p>
              <p className="text-xs mt-2">"Utilise la valeur du plan pour les bordures"</p>
              <p className="text-xs">"Applique une remise de 10%"</p>
              <p className="text-xs">"Change le gazon à 200 m²"</p>
            </div>
          )}
          
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] p-2 rounded-lg text-sm ${
                msg.role === 'user' 
                  ? 'bg-[#1E8B5A] text-white' 
                  : 'bg-white border'
              }`}>
                {msg.content}
                {msg.actions?.length > 0 && (
                  <div className="mt-1 pt-1 border-t border-gray-200">
                    {msg.actions.map((action, i) => (
                      <p key={i} className="text-xs text-green-600">✓ {action}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white border p-2 rounded-lg">
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Décrivez votre modification..."
            disabled={loading}
          />
          <Button onClick={sendMessage} disabled={loading || !input.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function PlanAnalysis() {
  const { user } = useAuth();
  
  // États pour les fichiers
  const [planFile, setPlanFile] = useState(null);
  const [planPreview, setPlanPreview] = useState(null);
  const [requestFile, setRequestFile] = useState(null);
  
  // États pour les résultats
  const [planResult, setPlanResult] = useState(null);
  const [requestResult, setRequestResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [extracting, setExtracting] = useState(false);
  
  // États pour les sélections
  const [selectedValues, setSelectedValues] = useState({});
  const [showOverlay, setShowOverlay] = useState(true);
  const [hourlyRate, setHourlyRate] = useState(75);
  const [showChat, setShowChat] = useState(false);
  
  // États pour la génération de devis
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [generating, setGenerating] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Handlers fichiers
  const handlePlanChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPlanFile(file);
      setPlanResult(null);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setPlanPreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setPlanPreview(null);
      }
    }
  };

  const handleRequestChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRequestFile(file);
      setRequestResult(null);
    }
  };

  // Analyser le plan
  const analyzePlan = async () => {
    if (!planFile) return;
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', planFile);
      formData.append('hourly_rate', hourlyRate);

      const response = await axios.post(`${API}/plans/analyze`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });

      setPlanResult(response.data);
      toast.success("Plan analysé !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur analyse plan");
    } finally {
      setAnalyzing(false);
    }
  };

  // Extraire la demande
  const extractRequest = async () => {
    if (!requestFile) return;
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append('file', requestFile);

      const response = await axios.post(`${API}/quotes/extract-request`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });

      setRequestResult(response.data);
      toast.success("Demande extraite !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur extraction demande");
    } finally {
      setExtracting(false);
    }
  };

  // Générer le devis final
  const generateQuote = async () => {
    if (Object.keys(selectedValues).length === 0) {
      toast.error("Veuillez sélectionner des valeurs pour le devis");
      return;
    }
    
    setGenerating(true);
    setQuoteResult(null);
    
    try {
      const response = await axios.post(`${API}/quotes/generate`, {
        selected_values: selectedValues,
        project_name: projectName,
        client_name: clientName,
        output_format: "xlsx",
        preserve_format: true
      }, { headers: getAuthHeaders() });
      
      setQuoteResult(response.data);
      toast.success("Devis généré avec succès !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la génération du devis");
    } finally {
      setGenerating(false);
    }
  };

  // Sélectionner une valeur
  const handleValueSelect = (elementType, source, value) => {
    setSelectedValues(prev => ({
      ...prev,
      [elementType]: { source, value }
    }));
  };

  // Obtenir les métrés agrégés du plan
  const getPlanMetrics = () => {
    if (!planResult?.totals) return {};
    // Le nouveau format utilise directement les noms de types comme clés
    const totals = planResult.totals;
    return {
      border: totals.border || totals.border_length_m || 0,
      paver: totals.paver || totals.paver_area_m2 || 0,
      lawn: totals.lawn || totals.lawn_area_m2 || 0,
      fence: totals.fence || totals.fence_length_m || 0,
      wall: totals.wall || totals.wall_length_m || 0,
      terrace: totals.terrace || totals.terrace_m2 || 0,
      earthwork: totals.earthwork || totals.earthwork_m3 || 0,
      tree: totals.tree || 0,
      shrub: totals.shrub || 0,
      hedge: totals.hedge || 0,
      perennial: totals.perennial || 0,
      drainage: totals.drainage || 0,
      irrigation: totals.irrigation || 0,
      pond: totals.pond || 0,
      lighting: totals.lighting || 0,
      stairs: totals.stairs || 0,
      gate: totals.gate || 0,
      building: totals.building || 0
    };
  };

  // Obtenir les métrés de la demande
  const getRequestMetrics = () => {
    if (!requestResult?.totals) return {};
    const totals = requestResult.totals;
    return {
      border: totals.border_m || totals.border || 0,
      paver: totals.paver_m2 || totals.paver || 0,
      lawn: totals.lawn_m2 || totals.lawn || 0,
      fence: totals.fence_m || totals.fence || 0,
      wall: totals.wall_m || totals.wall || 0,
      terrace: totals.terrace_m2 || totals.terrace || 0,
      earthwork: totals.earthwork_m3 || totals.earthwork || 0,
      tree: totals.tree || 0,
      shrub: totals.shrub || 0,
      hedge: totals.hedge || 0,
      perennial: totals.perennial || 0,
      drainage: totals.drainage || 0,
      irrigation: totals.irrigation || 0,
      pond: totals.pond || 0,
      lighting: totals.lighting || 0,
      stairs: totals.stairs || 0,
      gate: totals.gate || 0,
      building: totals.building || 0
    };
  };

  const planMetrics = getPlanMetrics();
  const requestMetrics = getRequestMetrics();

  // Éléments avec des valeurs
  const activeElements = Object.keys(elementLabels).filter(
    key => (planMetrics[key] || 0) > 0 || (requestMetrics[key] || 0) > 0
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analyse et comparaison</h1>
            <p className="text-gray-500">
              Comparez les métrés du plan avec ceux de la demande de chiffrage
            </p>
          </div>
        </div>

        {/* Zone d'upload */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload Plan */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <FileImage className="h-5 w-5 text-[#1E8B5A]" />
                Plan (image/PDF)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {planPreview ? (
                <div className="relative">
                  <PlanVisualization 
                    preview={planPreview} 
                    elements={planResult?.elements}
                    showOverlay={showOverlay}
                  />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setShowOverlay(!showOverlay)}>
                      {showOverlay ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center h-48 border-2 border-dashed rounded-xl cursor-pointer hover:border-gray-400 bg-gray-50">
                  <Upload className="w-8 h-8 mb-2 text-gray-400" />
                  <p className="text-sm text-gray-500">Plan (PNG, JPG, PDF)</p>
                  <input type="file" className="hidden" accept=".png,.jpg,.jpeg,.pdf" onChange={handlePlanChange} />
                </label>
              )}
              
              {/* Info échelle */}
              {planResult?.scale?.detected && (
                <div className="p-2 bg-blue-50 rounded-lg flex items-center gap-2">
                  <Scale className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-blue-700">
                    Échelle détectée: <strong>{planResult.scale.scale_ratio}</strong>
                    <span className="text-xs ml-2">({planResult.scale.confidence})</span>
                  </span>
                </div>
              )}
              
              <div className="flex gap-2">
                {planFile && (
                  <Button variant="outline" size="sm" onClick={() => { setPlanFile(null); setPlanPreview(null); setPlanResult(null); }}>
                    Nouveau
                  </Button>
                )}
                <Button 
                  onClick={analyzePlan} 
                  disabled={!planFile || analyzing}
                  className="flex-1 bg-[#1E8B5A] hover:bg-[#166B45]"
                >
                  {analyzing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calculator className="h-4 w-4 mr-2" />}
                  Analyser
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Upload Demande */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Demande de chiffrage
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {requestFile ? (
                <div className="p-4 bg-blue-50 rounded-xl flex items-center gap-3">
                  <FileText className="h-10 w-10 text-blue-600" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{requestFile.name}</p>
                    <p className="text-xs text-gray-500">{(requestFile.size / 1024).toFixed(1)} Ko</p>
                  </div>
                  {requestResult && <CheckCircle className="h-6 w-6 text-green-500" />}
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center h-48 border-2 border-dashed rounded-xl cursor-pointer hover:border-gray-400 bg-gray-50">
                  <Upload className="w-8 h-8 mb-2 text-gray-400" />
                  <p className="text-sm text-gray-500">Demande (Excel, Word, PDF)</p>
                  <input type="file" className="hidden" accept=".xlsx,.xls,.docx,.doc,.pdf" onChange={handleRequestChange} />
                </label>
              )}
              
              {/* Métrés extraits */}
              {requestResult?.metrics?.length > 0 && (
                <div className="p-2 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-700">
                    <CheckCircle className="h-4 w-4 inline mr-1" />
                    {requestResult.metrics.length} lignes de métré extraites
                  </p>
                </div>
              )}
              
              <div className="flex gap-2">
                {requestFile && (
                  <Button variant="outline" size="sm" onClick={() => { setRequestFile(null); setRequestResult(null); }}>
                    Nouveau
                  </Button>
                )}
                <Button 
                  onClick={extractRequest} 
                  disabled={!requestFile || extracting}
                  className="flex-1"
                  variant="outline"
                >
                  {extracting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                  Extraire métrés
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Comparaison des métrés */}
        {activeElements.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowUpDown className="h-5 w-5 text-purple-600" />
                Comparaison des métrés - Choisissez les valeurs à retenir
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activeElements.map(elementType => (
                  <MetricComparison
                    key={elementType}
                    elementType={elementType}
                    planValue={planMetrics[elementType]}
                    requestValue={requestMetrics[elementType]}
                    selectedValue={selectedValues[elementType]}
                    onValueSelect={(source, value) => handleValueSelect(elementType, source, value)}
                    onCustomValue={(value) => handleValueSelect(elementType, 'custom', value)}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Chat de modification */}
        {(planResult || requestResult) && (
          <div className="space-y-4">
            <Button
              variant="outline"
              onClick={() => setShowChat(!showChat)}
              className="w-full justify-between"
            >
              <span className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4" />
                Modifier le devis par chat
              </span>
              {showChat ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            
            {showChat && (
              <QuoteChat
                quoteData={{
                  planMetrics,
                  requestMetrics,
                  selectedValues,
                  hourlyRate
                }}
                onQuoteUpdate={(updated) => {
                  if (updated.selectedValues) {
                    setSelectedValues(updated.selectedValues);
                  }
                  toast.success("Devis mis à jour");
                }}
              />
            )}
          </div>
        )}

        {/* Résumé final */}
        {Object.keys(selectedValues).length > 0 && (
          <Card className="bg-gradient-to-br from-[#1E8B5A]/5 to-[#1E8B5A]/10 border-[#1E8B5A]/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-[#1E8B5A]" />
                Valeurs retenues pour le devis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(selectedValues).map(([type, data]) => (
                  <div key={type} className="p-3 bg-white rounded-lg shadow-sm">
                    <p className="text-xs text-gray-500">{elementLabels[type] || type}</p>
                    <p className="text-xl font-bold text-gray-800">{data.value}</p>
                    <p className="text-xs text-gray-400">{elementUnits[type] || ''} ({data.source})</p>
                  </div>
                ))}
              </div>
              
              {/* Bouton de génération de devis */}
              <div className="flex flex-col md:flex-row gap-4 pt-4 border-t">
                <div className="flex-1 space-y-2">
                  <label className="text-sm font-medium text-gray-700">Nom du projet</label>
                  <Input 
                    placeholder="Ex: Villa Dupont - Jardin" 
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <label className="text-sm font-medium text-gray-700">Nom du client</label>
                  <Input 
                    placeholder="Ex: M. Dupont" 
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    onClick={generateQuote}
                    disabled={generating || Object.keys(selectedValues).length === 0}
                    className="bg-[#1E8B5A] hover:bg-[#166B45] text-white"
                    data-testid="generate-quote-btn"
                  >
                    {generating ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <FileText className="h-4 w-4 mr-2" />
                    )}
                    Générer le devis
                  </Button>
                </div>
              </div>
              
              {/* Résultat de génération */}
              {quoteResult && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-green-800">Devis généré avec succès !</p>
                      <p className="text-sm text-green-600">
                        Total TTC: <strong>{quoteResult.quote_summary?.total_ttc?.toFixed(2)} CHF</strong>
                      </p>
                    </div>
                    <a
                      href={`${API.replace('/api', '')}${quoteResult.download_url}`}
                      download
                      className="px-4 py-2 bg-[#1E8B5A] text-white rounded-lg hover:bg-[#166B45] flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Télécharger
                    </a>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
